import React from 'react';
import { motion } from 'motion/react';
import { Laptop, Download, Zap, Shield, Cpu, ArrowLeft, Terminal, Layout } from 'lucide-react';
import { GlassCard, IconBox } from '../../components/SharedUI';

interface DesktopIntroProps {
  t: any;
  onBack: () => void;
  onInitialize: () => void;
}

export function DesktopIntro({ t, onBack, onInitialize }: DesktopIntroProps) {
  const downloads = [
    { platform: 'Windows', version: 'v2.4.0', format: '.exe', icon: '🪟' },
    { platform: 'macOS (Apple Silicon)', version: 'v2.4.0', format: '.dmg', icon: '🍎' },
    { platform: 'Linux', version: 'v2.4.0', format: '.AppImage', icon: '🐧' },
  ];

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="max-w-6xl mx-auto py-12 px-4 space-y-16"
    >
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
        <button 
          onClick={onBack}
          className="flex items-center gap-2 text-white/40 hover:text-celestial-saturn transition-colors uppercase tracking-widest text-xs font-bold"
        >
          <ArrowLeft size={16} />
          {t.backToEcosystem || '返回生态'}
        </button>
        <div className="flex items-center gap-3 px-4 py-2 bg-celestial-saturn/10 border border-celestial-saturn/20 rounded-full">
           <div className="w-2 h-2 rounded-full bg-celestial-saturn animate-pulse" />
           <span className="text-xs font-black text-celestial-saturn uppercase tracking-widest text-white/80">{t.xiaofengOSStableRelease || '小枫 OS v2.4.1 Stable Release'}</span>
        </div>
      </div>

      {/* Hero Section */}
      <div className="grid lg:grid-cols-2 gap-16 items-center">
        <div className="space-y-8">
          <div className="space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/5 border border-white/10 rounded-full text-xs font-bold text-white/40 uppercase tracking-widest">
              {t.highPerfWorkstationNode || '高性能工作站节点'}
            </div>
            <h1 className="text-6xl md:text-7xl font-black tracking-tighter leading-none italic uppercase">
              {t.initializeKineticDesktopCore || <>INITIALIZE <span className="text-celestial-saturn text-glow">KINETIC</span> <br/>DESKTOP CORE</>}
            </h1>
            <p className="text-xl text-white/40 leading-relaxed max-w-lg italic">
              {t.desktopHeroDesc || '将你的硬件转变为自主神经节点。零延迟、本地大语言模型执行、内核级操作系统自动化。'}
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-4">
             <button 
               onClick={onInitialize}
               className="group px-10 py-6 bg-white text-black font-black rounded-2xl hover:scale-105 active:scale-95 transition-all flex items-center justify-center gap-4 shadow-[0_0_50px_rgba(255,255,255,0.2)]"
             >
               <Layout size={24} className="text-celestial-saturn group-hover:rotate-12 transition-transform" />
               <div className="text-left">
                  <div className="text-lg leading-none">{t.bootVirtualOS || '启动虚拟系统'}</div>
                  <div className="text-xs opacity-60 tracking-wider">{t.browserDemoInstance || '浏览器演示实例'}</div>
               </div>
             </button>
             <button className="px-10 py-6 bg-white/5 text-white font-black rounded-2xl border border-white/10 hover:bg-white/10 transition-all flex items-center justify-center gap-4">
               <Download size={24} className="text-white/40" />
               <div className="text-left">
                  <div className="text-lg leading-none">{t.downloadNative || '下载原生版'}</div>
                  <div className="text-xs opacity-40 tracking-wider">{t.stableReleaseLabel || '稳定版 V2.4.1 发布'}</div>
               </div>
             </button>
          </div>
          
          <div className="flex items-center gap-8 pt-4">
            <div className="flex flex-col">
              <span className="text-xs font-black text-white/20 uppercase tracking-widest">{t.accelerationLabel || '加速'}</span>
              <div className="flex gap-2 mt-2">
                <div className="px-2 py-1 bg-white/5 border border-white/5 rounded text-xs font-bold text-white/40">NVIDIA CUDA</div>
                <div className="px-2 py-1 bg-white/5 border border-white/5 rounded text-xs font-bold text-white/40">APPLE METAL</div>
                <div className="px-2 py-1 bg-white/5 border border-white/5 rounded text-xs font-bold text-white/40">VULKAN</div>
              </div>
            </div>
          </div>
        </div>

        <div className="relative aspect-video rounded-[3rem] overflow-hidden border border-white/10 glass shadow-2xl group">
           <div className="absolute inset-0 bg-gradient-to-br from-celestial-saturn/20 via-transparent to-transparent group-hover:opacity-100 transition-opacity" />
           <div className="absolute inset-0 flex items-center justify-center">
              <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_center,rgba(255,200,80,0.2)_0%,transparent_70%)]" />
              <Laptop size={120} className="text-white/25 group-hover:scale-110 transition-transform duration-700" />
              <div className="absolute inset-0 flex flex-col items-center justify-center p-12 space-y-6">
                 <div className="relative">
                   <Terminal size={64} className="text-celestial-saturn opacity-50 relative z-10" />
                   <div className="absolute inset-0 blur-2xl bg-celestial-saturn/20 rounded-full animate-pulse" />
                 </div>
                 <div className="w-full max-w-sm space-y-3 bg-black/40 p-4 rounded-2xl border border-white/5 backdrop-blur-xl">
                    <div className="flex justify-between items-center px-1">
                      <span className="text-xs font-black text-white/20 uppercase tracking-widest">{t.kernelLoadSequence || '内核加载序列'}</span>
                      <span className="text-xs font-bold text-celestial-saturn">{t.stableLabel || '稳定'}</span>
                    </div>
                    <div className="h-1.5 w-full bg-white/5 rounded-full overflow-hidden">
                       <motion.div 
                         initial={{ width: 0 }}
                         animate={{ width: '100%' }}
                         transition={{ duration: 4, repeat: Infinity }}
                         className="h-full bg-gradient-to-r from-celestial-saturn/40 to-celestial-saturn" 
                        />
                    </div>
                    <div className="grid grid-cols-4 gap-1">
                      {[...Array(20)].map((_, i) => (
                        <div key={i} className="h-0.5 bg-white/10 rounded-full" />
                      ))}
                    </div>
                 </div>
              </div>
           </div>
        </div>
      </div>

      {/* Comparison Table */}
      <section className="glass p-12 rounded-[3.5rem] border border-white/5">
        <div className="max-w-4xl mx-auto space-y-12">
          <div className="text-center space-y-2">
            <h2 className="text-3xl font-black tracking-tighter uppercase italic">{t.nativeNodeSuperiority || <>Native Node <span className="text-celestial-saturn">Superiority</span></>}</h2>
            <p className="text-white/30 text-sm italic">{t.desktopSuperiorityDesc || '为何桌面客户端是神经架构师的首选'}</p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-12">
            <div className="space-y-6">
              <h4 className="text-xs font-black text-white/60 uppercase tracking-[0.3em] border-l-2 border-celestial-saturn pl-4">{t.standardBrowserEdition || '标准浏览器版'}</h4>
              <ul className="space-y-4">
                {[
                  t.browserItem1 || '依赖云端的神经同步',
                  t.browserItem2 || '受限沙箱环境',
                  t.browserItem3 || '标准 CPU/GPU 限制',
                  t.browserItem4 || '通用兼容性'
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-sm text-white/30 italic">
                    <div className="w-1.5 h-1.5 rounded-full bg-white/20" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="space-y-6">
              <h4 className="text-xs font-black text-celestial-saturn uppercase tracking-[0.3em] border-l-2 border-celestial-saturn pl-4">{t.lumiNativeKernelNode || '小枫 Native Kernel Node'}</h4>
              <ul className="space-y-4">
                {[
                  t.kernelItem1 || '全硅级本地大语言模型执行',
                  t.kernelItem2 || '直接操作系统与文件系统自动化',
                  t.kernelItem3 || '硬件加速（DirectX/Metal）',
                  t.kernelItem4 || '自主数据保险库（无遥测）'
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-sm text-white/90 font-bold italic">
                    <Zap size={14} className="text-celestial-saturn" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <div className="grid md:grid-cols-3 gap-8">
        <IntroFeature
          icon={<Cpu />}
          title={t.featureNvidiaAccel || 'NVIDIA/Metal 加速'}
          desc={t.featureNvidiaAccelDesc || 'Optimized kernels for maximum local inference speed using your hardware\'s full capability.'}
        />
        <IntroFeature
          icon={<Shield />}
          title={t.featureZeroKnowledge || '零知识同步'}
          desc={t.featureZeroKnowledgeDesc || '所有个人数据保留在本地保险库。仅元数据分片进入网格。'}
        />
        <IntroFeature
          icon={<Zap />}
          title={t.featureOSAutomation || '操作系统级自动化'}
          desc={t.featureOSAutomationDesc || '通过神经指令直接控制你的系统、文件和应用程序。'}
        />
      </div>

      {/* Downloads Section */}
      <section className="pt-8 space-y-8">
        <div className="text-center">
          <h2 className="text-3xl font-black tracking-widest uppercase">{t.systemBinaryDownloads || '系统二进制下载'}</h2>
          <p className="text-white/40 text-sm">{t.selectArchitecture || '选择你的架构以开始部署'}</p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {downloads.map((dl) => (
            <GlassCard key={dl.platform} className="p-8 border-white/5 hover:border-celestial-saturn/30 transition-colors group">
              <div className="flex justify-between items-start mb-6">
                <span className="text-4xl">{dl.icon}</span>
                <span className="text-xs font-bold py-1 px-3 bg-white/5 rounded-full text-white/40 uppercase tracking-widest">{t.stableReleaseBadge || '稳定版发布'}</span>
              </div>
              <div className="space-y-4">
                <div>
                  <h3 className="text-xl font-bold">{dl.platform}</h3>
                  <p className="text-xs text-white/30 font-mono">{dl.version} • {dl.format}</p>
                </div>
                <button className="w-full py-4 bg-white/5 hover:bg-white text-white hover:text-black font-black rounded-xl border border-white/10 transition-all flex items-center justify-center gap-2 group">
                  <Download size={18} className="group-hover:translate-y-0.5 transition-transform" />
                  {t.initDownload || '开始下载'}
                </button>
              </div>
            </GlassCard>
          ))}
        </div>
      </section>
    </motion.div>
  );
}

function IntroFeature({ icon, title, desc }: { icon: any, title: string, desc: string }) {
  return (
    <GlassCard className="p-8 space-y-6">
      <div className="w-12 h-12 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center text-celestial-saturn">
        {React.cloneElement(icon as React.ReactElement<any>, { size: 24 })}
      </div>
      <div className="space-y-2">
        <h3 className="text-lg font-bold uppercase tracking-tight">{title}</h3>
        <p className="text-sm text-white/40 leading-relaxed">{desc}</p>
      </div>
    </GlassCard>
  );
}
